import {
  ChevronDown,
  ChevronRight,
  FileCheck2,
  ListChecks,
  MapPin,
  MessageCircle,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import {
  buildStudioAgentWorkspaceModel,
  createAgentWorkspaceActiveTaskFromRequirement,
  findLatestRequirementForAnchor,
  findLatestSemanticPatchForRequirement,
  findProofsForSemanticPatch,
  buildAgentWorkspaceOntologyGeneratedApplicationBridgeFromProject,
  buildAgentWorkspaceReportBridgeFromProject,
  hasChartSidecarEvidence,
  hasReadyFormulaOracleEvidence,
  readProjectSourceToApplicationQuality,
  resolveAgentWorkspaceAnchorFromChatContext,
  sourceToApplicationWorkspaceStatus,
  type AgentWorkspaceActiveTask,
  type AgentWorkspaceModelStatus,
  type AgentWorkspaceOntologyGeneratedApplicationBridge,
  type StudioAgentWorkspaceModel,
} from '@/features/agent-workspace'
import { summarizeCogoCollabRequirement, type CogoCollabTransactionStatus } from '@/features/collaboration-kernel'
import type { AgenticUxTransaction } from '@/store/project-slice'
import { useStudioStore } from '../../../store/studio-store'
import { useStudioConversationSurfaceLabel } from './artifact-mode-switcher'
import { useStudioLocale } from '@/i18n'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'
import { COOGO_IDE_CHROME_COPY } from '../coogo-ide-chrome-copy'

interface CollaborationChromeCopy {
  conversationContext: string
  requestProgress: string
  ask: string
  review: string
  applied: string
  request: string
  target: string
  reviewSteps: string
  readyToShare: string
  noDraftChange: string
  draftFromRequest: string
  change: string
  idle: string
  directEdit: string
  reviewBeforeApply: string
  checksAndDetails: (checks: number, details: number) => string
  readinessChecks: string
  exportTargetsReady: (ready: number, total: number) => string
  status: Record<AgentWorkspaceModelStatus, string>
}

const COLLABORATION_CHROME_COPY: Record<LocaleCode, CollaborationChromeCopy> = {
  en: {
    conversationContext: 'Conversation context',
    requestProgress: 'Request progress',
    ask: 'Ask',
    review: 'Review',
    applied: 'Applied',
    request: 'Request',
    target: 'Target',
    reviewSteps: 'Review steps',
    readyToShare: 'Ready to share',
    noDraftChange: 'No draft change yet',
    draftFromRequest: 'Draft from request',
    change: 'change',
    idle: 'idle',
    directEdit: 'direct edit',
    reviewBeforeApply: 'review before apply',
    checksAndDetails: (checks, details) => `${checks} checks / ${details} details`,
    readinessChecks: 'Readiness checks',
    exportTargetsReady: (ready, total) => `${ready}/${total} export targets ready`,
    status: {
      ready: 'ready',
      warn: 'review',
      blocked: 'blocked',
      missing: 'waiting',
      planned: 'planned',
    },
  },
  ko: {
    conversationContext: '대화 컨텍스트',
    requestProgress: '요청 진행',
    ask: '질문',
    review: '검토',
    applied: '반영됨',
    request: '요청',
    target: '대상',
    reviewSteps: '검토 단계',
    readyToShare: '공유 준비',
    noDraftChange: '아직 변경 초안이 없습니다',
    draftFromRequest: '요청에서 만든 초안',
    change: '변경',
    idle: '대기',
    directEdit: '직접 편집',
    reviewBeforeApply: '반영 전 검토',
    checksAndDetails: (checks, details) => `검사 ${checks}개 / 세부 정보 ${details}개`,
    readinessChecks: '준비 상태 검사',
    exportTargetsReady: (ready, total) => `내보내기 대상 ${total}개 중 ${ready}개 준비`,
    status: {
      ready: '준비됨',
      warn: '검토',
      blocked: '차단됨',
      missing: '대기 중',
      planned: '계획됨',
    },
  },
  ja: {
    conversationContext: '会話コンテキスト',
    requestProgress: 'リクエストの進行',
    ask: '質問',
    review: '確認',
    applied: '反映済み',
    request: 'リクエスト',
    target: '対象',
    reviewSteps: '確認手順',
    readyToShare: '共有準備',
    noDraftChange: '変更案はまだありません',
    draftFromRequest: 'リクエストからの案',
    change: '変更',
    idle: '待機',
    directEdit: '直接編集',
    reviewBeforeApply: '反映前に確認',
    checksAndDetails: (checks, details) => `${checks}件の確認 / ${details}件の詳細`,
    readinessChecks: '準備状況の確認',
    exportTargetsReady: (ready, total) => `${total}件中${ready}件の出力先が準備済み`,
    status: {
      ready: '準備完了',
      warn: '確認',
      blocked: 'ブロック中',
      missing: '待機中',
      planned: '計画済み',
    },
  },
  zh: {
    conversationContext: '对话上下文',
    requestProgress: '请求进度',
    ask: '询问',
    review: '审核',
    applied: '已应用',
    request: '请求',
    target: '目标',
    reviewSteps: '审核步骤',
    readyToShare: '可供分享',
    noDraftChange: '尚无更改草案',
    draftFromRequest: '根据请求生成的草案',
    change: '更改',
    idle: '等待',
    directEdit: '直接编辑',
    reviewBeforeApply: '应用前审核',
    checksAndDetails: (checks, details) => `${checks}项检查 / ${details}项详情`,
    readinessChecks: '就绪检查',
    exportTargetsReady: (ready, total) => `${total}个导出目标中${ready}个已就绪`,
    status: {
      ready: '已就绪',
      warn: '审核',
      blocked: '已阻止',
      missing: '等待中',
      planned: '已计划',
    },
  },
  'zh-TW': {
    conversationContext: '對話內容',
    requestProgress: '請求進度',
    ask: '詢問',
    review: '審查',
    applied: '已套用',
    request: '請求',
    target: '目標',
    reviewSteps: '審查步驟',
    readyToShare: '可供分享',
    noDraftChange: '尚無變更草案',
    draftFromRequest: '根據請求產生的草案',
    change: '變更',
    idle: '等待',
    directEdit: '直接編輯',
    reviewBeforeApply: '套用前審查',
    checksAndDetails: (checks, details) => `${checks}項檢查 / ${details}項詳細資訊`,
    readinessChecks: '就緒檢查',
    exportTargetsReady: (ready, total) => `${total}個匯出目標中${ready}個已就緒`,
    status: {
      ready: '已就緒',
      warn: '審查',
      blocked: '已阻止',
      missing: '等待中',
      planned: '已規劃',
    },
  },
}

const STATUS_CLASS: Record<AgentWorkspaceModelStatus, string> = {
  ready: "border-border bg-muted text-foreground",
  warn: "border-border bg-muted text-muted-foreground",
  blocked: "border-destructive/25 bg-destructive/10 text-destructive",
  missing: "border-border bg-muted text-muted-foreground",
  planned: "border-border bg-muted text-muted-foreground",
};

function transactionStatus(transaction: AgenticUxTransaction | null): AgentWorkspaceModelStatus {
  if (!transaction) return 'warn'
  if (transaction.status === 'failed') return 'blocked'
  if (transaction.status === 'committed') return 'ready'
  return 'warn'
}

function ontologyRoleStatus(
  bridge: AgentWorkspaceOntologyGeneratedApplicationBridge | null,
  role: "qwen" | "gemma",
): AgentWorkspaceModelStatus {
  if (!bridge) return "missing";
  return bridge.modelRoles[role] ? bridge.status : "missing";
}

function activeTaskFromTransaction(
  transaction: AgenticUxTransaction | null,
  anchorId: string,
  evidenceRefs: string[],
): AgentWorkspaceActiveTask | undefined {
  if (!transaction) return undefined;
  if (transaction.status === "committed") return undefined;
  return {
    taskId: transaction.id,
    label: transaction.kind,
    intent: "fix",
    status: transactionStatus(transaction),
    owner: "cogo-agent",
    anchorId,
    semanticPatchId: transaction.id,
    validationPlan: [
      "Target linked",
      "Draft change is reviewed before apply",
      "Review checks update after approval",
    ],
    evidenceRefs,
    mayPatchCogoJsonDirectly: false,
  };
}

function combineReviewStatus(
  ...statuses: Array<AgentWorkspaceModelStatus | undefined>
): AgentWorkspaceModelStatus {
  const present = statuses.filter(
    (status): status is AgentWorkspaceModelStatus => Boolean(status),
  );
  if (present.length === 0 || present.every((status) => status === "missing"))
    return "missing";
  if (present.includes("blocked")) return "blocked";
  if (present.includes("warn")) return "warn";
  if (present.includes("planned")) return "planned";
  if (present.includes("missing")) return "warn";
  return "ready";
}

function userFacingTaskTitle(activeTask: AgentWorkspaceActiveTask | undefined, copy?: CollaborationChromeCopy): string {
  if (!activeTask) return copy?.noDraftChange ?? 'No draft change yet'
  if (activeTask.label === 'chat-spec-live-render') return copy?.draftFromRequest ?? 'Draft from request'
  return activeTask.label
    .replace(/semantic patch/gi, "draft change")
    .replace(/chat-spec-live-render/gi, "draft from request");
}

function userFacingValidationItem(item: string): string {
  return item
    .replace(/Cogo semantic patch/gi, "Draft change")
    .replace(/Semantic patch/gi, "Draft change")
    .replace(/Evidence/g, "Review check");
}

function humanizeTechnicalLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._/-]+/g, " ")
    .replace(/\bcogo json\b/gi, "project model")
    .trim();
}

const HELPER_SURFACE_LABELS: Record<string, string> = {
  wordgard: "notes",
  univer: "formula review",
};

function userFacingHelperLabel(id: string): string {
  return HELPER_SURFACE_LABELS[id] ?? "helper";
}

function userFacingHelperTitle(id: string): string {
  if (id === "wordgard") return "Linked requirement notes are helper-only";
  if (id === "univer") return "Formula and chart review is helper-only";
  return "Helper output does not write the canonical model";
}

function userFacingHelperStageTitle(title: string): string {
  return title
    .replace(/\bUniver\b/g, "Chart review")
    .replace(/\bWordGard\b/g, "Requirement notes");
}

function isRequirementApproved(status: string | undefined): boolean {
  return status === "patched";
}

function collaborationModelStatus(
  status: CogoCollabTransactionStatus | undefined,
): AgentWorkspaceModelStatus {
  if (!status) return "missing";
  if (status === "conflicted") return "blocked";
  if (status === "applied") return "ready";
  if (status === "rejected") return "missing";
  return "warn";
}

type CollaborationStepId = "ask" | "review" | "applied";

const COLLABORATION_STEPS: Array<{
  id: CollaborationStepId;
  label: string;
}> = [
  { id: "ask", label: "Ask" },
  { id: "review", label: "Review" },
  { id: "applied", label: "Applied" },
];

function stepIndex(step: CollaborationStepId): number {
  return COLLABORATION_STEPS.findIndex((entry) => entry.id === step);
}

function CollaborationStepStrip({
  currentStep,
  copy,
}: {
  currentStep: CollaborationStepId
  copy: CollaborationChromeCopy
}) {
  const currentIndex = stepIndex(currentStep)
  const steps = COLLABORATION_STEPS.map((step) => ({
    ...step,
    label: copy[step.id],
  }))
  return (
    <ol
      className="mt-3 grid grid-cols-3 gap-1"
      aria-label={copy.requestProgress}
      data-testid="agent-collaboration-step-strip"
    >
      {steps.map((step, index) => {
        const complete = index < currentIndex
        const current = step.id === currentStep
        return (
          <li key={step.id} className="min-w-0">
            <div
              className={`flex h-7 items-center justify-center rounded-md border px-2 text-[11px] font-medium ${
                current
                  ? "border-foreground bg-foreground text-background"
                  : complete
                    ? "border-border bg-muted text-foreground"
                    : "border-border bg-muted/40 text-muted-foreground"
              }`}
              aria-current={current ? "step" : undefined}
            >
              <span className="truncate">{step.label}</span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function CurrentAction({
  title,
}: {
  title: string;
}) {
  return (
    <div
      className="mt-2 flex min-w-0 items-center"
      data-testid="agent-collaboration-current-action"
    >
      <div className="inline-flex min-h-7 items-center rounded-full border border-border bg-muted/40 px-2.5 text-xs font-semibold leading-none text-foreground">
        {title}
      </div>
    </div>
  );
}

export function buildChatAgentWorkspaceModel(input: {
  currentProject: unknown;
  chatContext: unknown;
  agenticUxTransaction: AgenticUxTransaction | null;
}): StudioAgentWorkspaceModel {
  const currentProject = input.currentProject;
  const hasProject = Boolean(currentProject);
  const activeAnchor = resolveAgentWorkspaceAnchorFromChatContext(
    input.chatContext,
  );
  const activeRequirement = findLatestRequirementForAnchor(
    currentProject,
    activeAnchor.anchorId,
  );
  const activeSemanticPatch = activeRequirement
    ? findLatestSemanticPatchForRequirement(
        currentProject,
        activeRequirement.requirementId,
      )
    : null;
  const activeProofs = activeSemanticPatch
    ? findProofsForSemanticPatch(currentProject, activeSemanticPatch.patchId)
    : [];
  const activeTaskFromRequirement = activeRequirement
    ? createAgentWorkspaceActiveTaskFromRequirement(
        activeRequirement,
        activeAnchor,
        {
          semanticPatchId: activeSemanticPatch?.patchId,
          validationPlan: activeSemanticPatch?.validationPlan,
          evidenceRefs: [
            ...(activeSemanticPatch?.evidenceRefs ?? []),
            ...activeProofs.flatMap((proof) => proof.evidenceRefs),
          ],
        },
      )
    : undefined;
  const activeTask =
    activeTaskFromRequirement ??
    activeTaskFromTransaction(
      input.agenticUxTransaction,
      activeAnchor.anchorId,
      activeAnchor.evidenceRefs,
    );
  const sourceToApplicationQuality =
    readProjectSourceToApplicationQuality(currentProject);
  const reportWorkspace =
    buildAgentWorkspaceReportBridgeFromProject(currentProject);
  const ontologyGeneratedApplication =
    buildAgentWorkspaceOntologyGeneratedApplicationBridgeFromProject(
      currentProject,
    );
  const qwenStatus = sourceToApplicationWorkspaceStatus(
    sourceToApplicationQuality,
    "qwen",
  );
  const gemmaStatus = sourceToApplicationWorkspaceStatus(
    sourceToApplicationQuality,
    "gemma",
  );
  const deterministicStatus = sourceToApplicationWorkspaceStatus(
    sourceToApplicationQuality,
    "codex-deterministic",
  );
  return buildStudioAgentWorkspaceModel({
    title: "Intent collaboration",
    activeAnchor,
    activeRequirement,
    activeTask,
    qwenStatus: hasProject
      ? qwenStatus === "missing"
        ? ontologyRoleStatus(ontologyGeneratedApplication, "qwen")
        : qwenStatus
      : "missing",
    gemmaStatus: hasProject
      ? gemmaStatus === "missing"
        ? ontologyRoleStatus(ontologyGeneratedApplication, "gemma")
        : gemmaStatus
      : "missing",
    deterministicStatus: hasProject
      ? deterministicStatus === "missing"
        ? (ontologyGeneratedApplication?.status ?? "missing")
        : deterministicStatus
      : "missing",
    previewAnchorStatus: hasProject ? "ready" : "missing",
    sourceModelStatus: hasProject ? "warn" : "missing",
    semanticPatchStatus:
      activeTask?.status ?? transactionStatus(input.agenticUxTransaction),
    exportProofStatus: hasProject ? "warn" : "missing",
    wordgardReviewed: true,
    chartSidecarReady: hasChartSidecarEvidence(currentProject),
    univerFormulaOracleReady: hasReadyFormulaOracleEvidence(currentProject),
    reportWorkspace,
    ontologyGeneratedApplication,
  });
}

function StatusPill({
  label,
  status,
  title,
  statusCopy,
}: {
  label: string
  status: AgentWorkspaceModelStatus
  title: string
  statusCopy?: Record<AgentWorkspaceModelStatus, string>
}) {
  return (
    <span
      title={title}
      className={`inline-flex min-w-0 items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] ${STATUS_CLASS[status]}`}
    >
      <span className="truncate">{label}</span>
      <span className="shrink-0">{statusCopy?.[status] ?? status}</span>
    </span>
  );
}

function LoopPanel({
  icon,
  label,
  title,
  children,
}: {
  icon: ReactNode;
  label: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0 rounded-md border border-border bg-card px-3 py-2">
      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div
        className="truncate text-xs font-semibold text-foreground"
        title={title}
      >
        {title}
      </div>
      {children}
    </section>
  );
}

export function AgentCollaborationSummary({ model }: { model?: StudioAgentWorkspaceModel }) {
  const { locale } = useStudioLocale()
  const chromeCopy = COOGO_IDE_CHROME_COPY[locale]
  const collaborationCopy = COLLABORATION_CHROME_COPY[locale]
  const currentProject = useStudioStore((s) => s.currentProject)
  const chatContext = useStudioStore((s) => s.chatContext)
  const activeRailItem = useStudioStore((s) => s.activeRailItem)
  const surfaceLabel = useStudioConversationSurfaceLabel(activeRailItem)
  const agenticUxTransaction = useStudioStore((s) => s.agenticUxTransaction)
  const derivedModel = useMemo(
    () =>
      model ??
      buildChatAgentWorkspaceModel({
        currentProject,
        chatContext,
        agenticUxTransaction,
      }),
    [agenticUxTransaction, chatContext, currentProject, model],
  );

  const qwen = derivedModel.collaborators.find(
    (entry) => entry.id === "qwen-reviewer",
  );
  const gemma = derivedModel.collaborators.find(
    (entry) => entry.id === "gemma-reviewer",
  );
  const deterministic = derivedModel.collaborators.find(
    (entry) => entry.id === "codex-deterministic",
  );
  const semanticPatch = derivedModel.evidenceLanes.find(
    (entry) => entry.id === "semantic-patch",
  );
  const reportFormula = derivedModel.evidenceLanes.find(
    (entry) => entry.id === "report-formula-oracle",
  );
  const reportChart = derivedModel.evidenceLanes.find(
    (entry) => entry.id === "report-chart-semantics",
  );
  const generatedApplication = derivedModel.ontologyGeneratedApplication;
  const wordgard = derivedModel.documentSurfacePolicy.surfaces.find(
    (entry) => entry.id === "wordgard",
  );
  const univer = derivedModel.documentSurfacePolicy.surfaces.find(
    (entry) => entry.id === "univer",
  );
  const helperStage =
    derivedModel.chartIntegrationPlan.stages.find(
      (stage) => stage.ownerSurface === "univer" && stage.status !== "ready",
    ) ??
    derivedModel.chartIntegrationPlan.stages.find(
      (stage) => stage.ownerSurface === "univer",
    );
  const activeRequirement = derivedModel.activeRequirement;
  const activeTask = derivedModel.activeTask;
  const collaborationSummary = activeRequirement
    ? summarizeCogoCollabRequirement(
        currentProject,
        activeRequirement.requirementId,
      )
    : null;
  const proposalStatus = collaborationModelStatus(
    collaborationSummary?.transaction?.status,
  );
  const hasProposalConflict = proposalStatus === "blocked";
  const validationPlan =
    activeTask?.validationPlan && activeTask.validationPlan.length > 0
      ? activeTask.validationPlan
      : ['Target linked', 'Review proposed change', 'Refresh the app after approval']
  const reviewStatus = combineReviewStatus(qwen?.status, gemma?.status)
  const evidenceRefCount = new Set([...derivedModel.activeAnchor.evidenceRefs, ...(activeTask?.evidenceRefs ?? [])])
    .size
  const anchorLabel = derivedModel.activeAnchor.label || chromeCopy.generatedApp
  const displayedAnchorLabel = anchorLabel.toLocaleLowerCase('en') === 'workspace' ? chromeCopy.workspace : anchorLabel
  const requestText =
    activeRequirement?.body ?? (activeTask ? userFacingTaskTitle(activeTask, collaborationCopy) : collaborationCopy.ask)
  const approved = isRequirementApproved(activeRequirement?.status)
  const currentStep: CollaborationStepId =
    approved || proposalStatus === "ready"
      ? "applied"
      : activeTask || activeRequirement
        ? "review"
        : "ask";
  const currentActionTitle = hasProposalConflict
    ? collaborationCopy.review
    : currentStep === 'applied'
      ? collaborationCopy.applied
      : currentStep === 'review'
        ? collaborationCopy.review
        : collaborationCopy.ask
  const [detailsOpen, setDetailsOpen] = useState(false)

  return (
    <section
      data-testid="agent-collaboration-summary"
      className="shrink-0 rounded-lg border border-border bg-background px-3 py-3"
      aria-label={collaborationCopy.conversationContext}
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-muted">
            <Sparkles className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase leading-3 text-muted-foreground">{surfaceLabel}</div>
            <div
              className="mt-0.5 break-words text-sm font-semibold leading-5 text-foreground"
              title={displayedAnchorLabel}
            >
              {displayedAnchorLabel}
            </div>
          </div>
        </div>
      </div>

      <CurrentAction title={currentActionTitle} />

      {activeRequirement && (
        <div className="mt-2 min-w-0 rounded-md bg-muted/40 px-2 py-1.5">
          <div className="text-[10px] font-semibold uppercase leading-3 text-muted-foreground">
            {collaborationCopy.request}
          </div>
          <div className="mt-0.5 line-clamp-2 break-words text-[11px] leading-4 text-foreground">{requestText}</div>
        </div>
      )}

      {collaborationSummary?.message && (
        <div
          className={`mt-2 rounded-md border px-2 py-1.5 text-[11px] leading-4 ${
            hasProposalConflict
              ? "border-destructive/30 bg-destructive/10 text-destructive"
              : "border-border bg-card text-muted-foreground"
          }`}
          data-testid="agent-collaboration-proposal-status"
        >
          {collaborationSummary.message}
        </div>
      )}

      <div className="mt-2 flex min-w-0 items-center gap-2">
        <button
          type="button"
          className="ml-auto inline-flex min-h-8 shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
          aria-expanded={detailsOpen}
          aria-controls="agent-workspace-loop"
          onClick={() => setDetailsOpen((open) => !open)}
        >
          {detailsOpen ? (
            <ChevronDown className="h-3 w-3" aria-hidden />
          ) : (
            <ChevronRight className="h-3 w-3" aria-hidden />
          )}
          {detailsOpen ? chromeCopy.close : chromeCopy.details}
        </button>
      </div>

      {detailsOpen && (
        <div id="agent-workspace-loop" data-testid="agent-workspace-loop">
          <CollaborationStepStrip currentStep={currentStep} copy={collaborationCopy} />
          <div className="mt-2 grid min-w-0 gap-2 lg:grid-cols-2">
            <LoopPanel
              icon={<MapPin className="h-3 w-3" aria-hidden />}
              label={collaborationCopy.target}
              title={humanizeTechnicalLabel(derivedModel.activeAnchor.kind)}
            >
              <div className="mt-1 truncate font-mono text-[10px] text-muted-foreground">
                {derivedModel.activeAnchor.label}
              </div>
              <div className="mt-1 flex min-w-0 flex-wrap gap-1">
                {derivedModel.activeAnchor.targetFormats.map((format) => (
                  <span
                    key={format}
                    className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
                  >
                    {format}
                  </span>
                ))}
              </div>
            </LoopPanel>

            <LoopPanel
              icon={<MessageCircle className="h-3 w-3" aria-hidden />}
              label={collaborationCopy.request}
              title={userFacingTaskTitle(activeTask, collaborationCopy)}
            >
              <div className="mt-1 flex min-w-0 flex-wrap gap-1">
                <StatusPill
                  label={activeTask ? collaborationCopy.change : collaborationCopy.idle}
                  status={activeTask?.status ?? 'planned'}
                  title="Current anchored agent task"
                  statusCopy={collaborationCopy.status}
                />
                <span className="rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                  {activeTask?.mayPatchCogoJsonDirectly
                    ? collaborationCopy.directEdit
                    : collaborationCopy.reviewBeforeApply}
                </span>
              </div>
            </LoopPanel>

            <LoopPanel
              icon={<ListChecks className="h-3 w-3" aria-hidden />}
              label={collaborationCopy.reviewSteps}
              title={collaborationCopy.checksAndDetails(validationPlan.length, evidenceRefCount)}
            >
              <div className="mt-1 space-y-0.5">
                {validationPlan.slice(0, 3).map((item) => (
                  <div
                    key={item}
                    className="flex min-w-0 items-center gap-1 text-[10px] text-muted-foreground"
                  >
                    <FileCheck2 className="h-3 w-3 shrink-0" aria-hidden />
                    <span className="truncate">
                      {userFacingValidationItem(item)}
                    </span>
                  </div>
                ))}
              </div>
            </LoopPanel>

            <LoopPanel
              icon={<ShieldCheck className="h-3 w-3" aria-hidden />}
              label={collaborationCopy.readyToShare}
              title={
                derivedModel.reportWorkspace
                  ? collaborationCopy.exportTargetsReady(
                      derivedModel.reportWorkspace.readyTargetCount,
                      derivedModel.reportWorkspace.targetCount,
                    )
                  : collaborationCopy.readinessChecks
              }
            >
              <div className="mt-1 flex min-w-0 flex-wrap gap-1">
                <StatusPill
                  label="review"
                  status={reviewStatus}
                  title="Independent review signal"
                  statusCopy={collaborationCopy.status}
                />
                <StatusPill
                  label="builder"
                  status={deterministic?.status ?? 'missing'}
                  title="Final builder check"
                  statusCopy={collaborationCopy.status}
                />
                {generatedApplication && (
                  <StatusPill
                    label="scenario"
                    status={generatedApplication.status}
                    title="Source-backed scenario check"
                    statusCopy={collaborationCopy.status}
                  />
                )}
                <StatusPill
                  label="draft"
                  status={semanticPatch?.status ?? 'missing'}
                  title="Proposed change state"
                  statusCopy={collaborationCopy.status}
                />
                {collaborationSummary?.transaction && (
                  <StatusPill
                    label="proposal"
                    status={proposalStatus}
                    title="Proposal safety state"
                    statusCopy={collaborationCopy.status}
                  />
                )}
                {derivedModel.reportWorkspace && (
                  <StatusPill
                    label={`export ${derivedModel.reportWorkspace.readyTargetCount}/${derivedModel.reportWorkspace.targetCount}`}
                    status={derivedModel.reportWorkspace.exportGateStatus}
                    title={
                      derivedModel.reportWorkspace.nativeExportReady
                        ? "Native export is linked and ready"
                        : "Native export still needs confirmation"
                    }
                    statusCopy={collaborationCopy.status}
                  />
                )}
                {reportFormula && (
                  <StatusPill
                    label="formula"
                    status={reportFormula.status}
                    title="Formula calculation check"
                    statusCopy={collaborationCopy.status}
                  />
                )}
                {reportChart && (
                  <StatusPill
                    label="chart"
                    status={reportChart.status}
                    title="Chart semantic check"
                    statusCopy={collaborationCopy.status}
                  />
                )}
                {wordgard && (
                  <StatusPill
                    label={userFacingHelperLabel(wordgard.id)}
                    status={wordgard.status}
                    title={userFacingHelperTitle(wordgard.id)}
                    statusCopy={collaborationCopy.status}
                  />
                )}
                {univer && (
                  <StatusPill
                    label={userFacingHelperLabel(univer.id)}
                    status={univer.status}
                    title={userFacingHelperTitle(univer.id)}
                    statusCopy={collaborationCopy.status}
                  />
                )}
              </div>
              {helperStage && (
                <div className="mt-1 truncate font-mono text-[10px] text-muted-foreground">
                  {userFacingHelperStageTitle(helperStage.title)}
                </div>
              )}
            </LoopPanel>
          </div>
        </div>
      )}
    </section>
  );
}
